import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import FormError from '../../components/FormError';

type ImportResult = { batchId: string; total: number; imported: number; duplicated: number; errors: string[] };
type UploadResult = ImportResult & { route: string; contasVinculadas: number; pdfs: number; demo: boolean };


export default function Importar() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [uploadInfo, setUploadInfo] = useState<UploadResult | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  function parseLines(text: string): Record<string, unknown>[] {
    const rows: Record<string, unknown>[] = [];
    for (const line of text.split(/\r?\n/)) {
      const t = line.trim();
      if (!t || t.startsWith('#')) continue;
      const parts = t.split(/[;,\t]/).map((s) => s.trim());
      if (/^empresa/i.test(parts[0] ?? '')) continue;
      const [companyName, cnpj, phone, city, state, carrier, plan, spend, lines] = parts;
      if (!companyName) continue;
      rows.push({
        companyName, cnpj: cnpj || undefined, phone: phone || undefined,
        city: city || undefined, state: state || undefined,
        currentCarrier: carrier || undefined, currentPlan: plan || undefined,
        monthlySpend: spend ? Number(spend.replace(/\./g, '').replace(',', '.')) : undefined,
        lineCount: lines ? Number(lines) : undefined,
      });
    }
    return rows;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const rows = parseLines(text);
    if (!rows.length) { setFormError('Nenhuma linha válida. Formato: empresa;cnpj;telefone;cidade;uf;operadora;plano;gasto;linhas'); return; }
    setBusy(true);
    setFormError(null);
    try {
      const r = await api.post<ImportResult>('/batches', { name: name || 'Batch sem nome', rows });
      setResult(r);
      toast.success(`Batch importado: ${r.imported} novos, ${r.duplicated} reincidentes.`);
    } catch (err) {
      const e = err as { message?: string; details?: Record<string, string[]> };
      const first = e.details ? Object.entries(e.details)[0] : null;
      setFormError(first ? `${first[0]}: ${first[1][0]}` : e.message ?? 'Falha na importação');
    } finally {
      setBusy(false);
    }
  }

  async function onUpload(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const file = fd.get('package') as File | null;
    if (!file || file.size === 0) { setFormError('Escolha o arquivo/pacote para subir.'); return; }
    setBusy(true);
    setFormError(null);
    try {
      const r = await api.multipart<UploadResult>('/batches/upload', fd as unknown as FormData);
      setResult({
        batchId: r.batchId, total: r.total, imported: r.imported,
        duplicated: r.duplicated, errors: r.errors as string[],
      });
      setUploadInfo(r);
      toast.success(r.demo
        ? `Pacote lido em MODO DEMO (sem chave de IA): ${r.imported} leads de exemplo. Configure a chave em Configurações.`
        : `Pacote "${r.route}" lido: ${r.imported} novos, ${r.duplicated} reincidentes, ${r.contasVinculadas} contas vinculadas.`);
    } catch (err) {
      const e = err as { message?: string; details?: Record<string, string[]> };
      const first = e.details ? Object.entries(e.details)[0] : null;
      setFormError(first ? `${first[0]}: ${first[1][0]}` : e.message ?? 'Falha no upload do pacote');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-3xl">
      <header>
        <p className="text-xs font-semibold tracking-widest text-[var(--text-dim)] uppercase">Leads</p>
        <h1 className="mt-1 text-xl font-semibold tracking-tight">Importar batch da Claro</h1>
      </header>
      <form onSubmit={onUpload} className="mt-4 space-y-3 rounded-lg border border-[var(--border)] bg-white p-4">
        <header className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 text-sm font-semibold"><Upload size={14} /> Upload do pacote da Claro</h2>
            <p className="mt-0.5 text-xs text-[var(--text-dim)]">É assim que a operadora envia: planilha + contas em PDF. Suba o zip/pasta inteira (ou só a planilha) que a leitura completa, fiel e dedip é automática.</p>
          </div>
        </header>
        <label className="block">
          <span className="mb-1 block text-sm font-medium">Pacote (zip com planilha + contas) · ou xlsx, docx, csv, txt, pdf, imagem</span>
          <input name="package" type="file"
            accept=".zip,.xlsx,.docx,.csv,.txt,.pdf,.png,.jpg,.jpeg,.webp"
            className="w-full text-sm text-[var(--text-dim)] file:mr-3 file:h-10 file:cursor-pointer file:rounded-lg file:border-0 file:bg-[var(--bg-subtle)] file:px-4 file:text-sm file:font-medium file:text-[var(--text)] hover:file:bg-[var(--border)]" />
          <span className="mt-1 block text-xs text-[var(--text-dim)]">Até 60MB. Imagem (print) lida com IA visão; documento/texto estruturados por IA quando não tabulares.</span>
        </label>
        <input type="text" name="name" placeholder="Nome do batch (Ex: B31 — Outubro/2026)" className="w-full h-10 rounded-lg border border-[var(--border-strong)] bg-white px-3 text-sm" />
        <button type="submit" disabled={busy} className="h-10 rounded-lg bg-[var(--accent)] px-5 text-sm font-semibold text-white hover:bg-[#c40a20] disabled:opacity-50">
          {busy ? 'Lendo pacote...' : 'Subir e organizar'}
        </button>
        {uploadInfo && (
          <p className="text-xs text-[var(--text-dim)]">
            Modo de leitura: <strong>{uploadInfo.route}</strong> · Contas anexadas: {uploadInfo.pdfs > 0 ? `${uploadInfo.contasVinculadas}/${uploadInfo.pdfs} vinculadas` : 'nenhum pdf no pacote'}
          </p>
        )}
        <FormError error={formError} onClear={() => setFormError(null)} />
      </form>
      <details className="mt-2 text-sm">
        <summary className="cursor-pointer text-sm text-[var(--text-dim)] hover:text-[var(--text)]">Alternativa: colar linhas / planilha copiada (fomato empresa;cnpj;telefone;...)</summary>
      <form onSubmit={onSubmit} className="mt-4 space-y-4">

        <div>
          <label htmlFor="batch-name" className="mb-1 block text-sm font-medium">Nome do batch</label>
          <input id="batch-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Base RJ — Outubro 2026"
            className="w-full h-10 rounded-lg border border-[var(--border-strong)] bg-white px-3 text-sm" />
        </div>
        <div>
          <label htmlFor="batch-rows" className="mb-1 block text-sm font-medium">Linhas (uma por lead)</label>
          <textarea id="batch-rows" value={text} onChange={(e) => setText(e.target.value)} rows={10}
            placeholder={'empresa;cnpj;telefone;cidade;uf;operadora;plano;gasto/mês;linhas\nACME LTDA;12345678000199;(21) 22223333;Rio de Janeiro;RJ;Vivo;Empresarial 5G;1850,00;12'}
            className="w-full rounded-lg border border-[var(--border-strong)] bg-white p-3 font-mono text-xs" />
          <p className="mt-1 text-xs text-[var(--text-dim)]">Colar direto do Excel/Sheets (separado por ; ou tab). Duplicados viram atualização, não lead novo.</p>
        </div>
        <button type="submit" disabled={busy}
          className="h-10 rounded-lg bg-[var(--accent)] px-5 text-sm font-semibold text-white hover:bg-[#c40a20] transition-colors disabled:opacity-50">
          {busy ? 'Importando...' : 'Importar batch'}
        </button>
        <FormError error={formError} onClear={() => setFormError(null)} />
      </form>
      </details>
      {result && (
        <div className="mt-4 rounded-lg border border-[var(--border)] bg-white p-4 text-sm">
          <h2 className="font-medium">Resultado do batch</h2>
          <ul className="mt-2 space-y-1 text-[var(--text-dim)]">
            <li>Linhas recebidas: {result.total}</li>
            <li>Leads novos: <strong className="text-[var(--text)]">{result.imported}</strong></li>
            <li>Reincidentes (atualizados): <strong className="text-[var(--text)]">{result.duplicated}</strong></li>
            {result.errors.length > 0 && <li className="text-red-claro">Erros: {result.errors.join(' | ')}</li>}
          </ul>
          <div className="mt-3 flex gap-2">
            <button onClick={() => navigate('/painel/leads')} className="h-8 rounded-md border border-[var(--border-strong)] px-3 text-xs hover:bg-[var(--bg-subtle)]">Ver leads</button>
          </div>
        </div>
      )}
    </div>
  );
}
